// Utility functions for persisting workflow sessions to sessionStorage (uses optimized sessionStorage with debouncing)

import { debouncedSetItem, immediateSetItem, getItem, removeItem as optimizedRemoveItem, clearPendingWrite } from './optimizedSessionStorage';

const WORKFLOW_SESSIONS_KEY = 'workflowSessions';
const ACTIVE_WORKFLOW_SESSION_ID_KEY = 'activeWorkflowSessionId';

// Save workflow sessions to sessionStorage (with debouncing)
export const saveWorkflowSessions = (sessions, sessionOrder, activeSessionId, immediate = false) => {
  try {
    const sessionsData = {
      sessions: {},
      sessionOrder,
      activeSessionId,
      timestamp: Date.now(),
    };

    // Convert sessions object to a serializable format
    Object.keys(sessions).forEach(sessionId => {
      const session = sessions[sessionId];
      sessionsData.sessions[sessionId] = {
        sessionId: session.sessionId,
        name: session.name,
        createdAt: session.createdAt,
        state: session.state, // This contains the workflow state
      };
    });

    // Use immediate write for workflow sessions (critical data)
    if (immediate) {
      immediateSetItem(WORKFLOW_SESSIONS_KEY, sessionsData);
    } else {
      debouncedSetItem(WORKFLOW_SESSIONS_KEY, sessionsData, 300);
    }
  } catch (error) {
    console.error('Error saving workflow sessions to sessionStorage:', error);
  }
};

// Load workflow sessions from sessionStorage
export const loadWorkflowSessions = () => {
  try {
    const data = getItem(WORKFLOW_SESSIONS_KEY);
    if (!data) {
      return null;
    }

    return {
      sessions: data.sessions || {},
      sessionOrder: data.sessionOrder || [],
      activeSessionId: data.activeSessionId || null,
    };
  } catch (error) {
    console.error('Error loading workflow sessions from sessionStorage:', error);
    return null;
  }
};

// Save active workflow session ID (immediate write - critical data)
export const saveActiveWorkflowSessionId = (sessionId) => {
  try {
    immediateSetItem(ACTIVE_WORKFLOW_SESSION_ID_KEY, sessionId);
  } catch (error) {
    console.error('Error saving active workflow session ID:', error);
  }
};

// Load active workflow session ID
export const loadActiveWorkflowSessionId = () => {
  try {
    return getItem(ACTIVE_WORKFLOW_SESSION_ID_KEY);
  } catch (error) {
    console.error('Error loading active workflow session ID:', error);
    return null;
  }
};

// Clear all workflow sessions from sessionStorage
export const clearWorkflowSessions = () => {
  try {
    optimizedRemoveItem(WORKFLOW_SESSIONS_KEY);
    optimizedRemoveItem(ACTIVE_WORKFLOW_SESSION_ID_KEY);
  } catch (error) {
    console.error('Error clearing workflow sessions from sessionStorage:', error);
  }
};

// Per-workflow sessionStorage keys - sessionId is already "workflow-1", so just append key
export const getWorkflowStorageKey = (sessionId, key) => {
  if (!sessionId) return null;
  // sessionId is already "workflow-1", so just append "-{key}"
  return `${sessionId}-${key}`;
};

// Save lastJobId for a specific workflow session (immediate write to prevent race conditions when switching workflows)
export const saveWorkflowLastJobId = (sessionId, jobId) => {
  try {
    const key = getWorkflowStorageKey(sessionId, 'lastJobId');
    if (key) {
      // Use immediate write to prevent race conditions with debounced writes This ensures job IDs are saved immediately for the correct workflow
      immediateSetItem(key, jobId);
    }
  } catch (error) {
    console.error('Error saving workflow lastJobId:', error);
  }
};

// Load lastJobId for a specific workflow session
export const loadWorkflowLastJobId = (sessionId) => {
  try {
    const key = getWorkflowStorageKey(sessionId, 'lastJobId');
    if (key) {
      return getItem(key);
    }
  } catch (error) {
    console.error('Error loading workflow lastJobId:', error);
  }
  return null;
};

// Clear lastJobId for a specific workflow session CRITICAL: Use immediate removal to prevent race conditions
export const clearWorkflowLastJobId = (sessionId) => {
  try {
    const key = getWorkflowStorageKey(sessionId, 'lastJobId');
    if (key) {
      optimizedRemoveItem(key);
      console.log(`[Workflow:${sessionId}] Cleared jobId from sessionStorage`);
    }
  } catch (error) {
    console.error('Error clearing workflow lastJobId:', error);
  }
};

// NOTE: We don't save originalExtractedData separately for workflows Instead, we use the tab's editableData as the source of truth This reduces sessionStorage bloat and keeps data in one place

// Load originalExtractedData from tab data (not separate workflow storage)
export const loadWorkflowOriginalExtractedData = (sessionId) => {
  // Workflow data comes from tabs, not separate storage Return null to indicate we should use tab data instead
  return null;
};

// Find which workflow session a jobId belongs to (by checking all workflows) OPTIMIZED: Returns the workflow session ID that owns this jobId, or null if not found
export const findWorkflowByJobId = (jobId) => {
  if (!jobId) return null;
  
  try {
    // Check all possible workflow session IDs (workflow-1, workflow-2, etc.) We'll check up to 100 workflows (reasonable limit)
    for (let i = 1; i <= 100; i++) {
      const sessionId = `workflow-${i}`;
      const key = getWorkflowStorageKey(sessionId, 'lastJobId');
      if (key) {
        const storedJobId = getItem(key);
        if (storedJobId === jobId) {
          return sessionId;
        }
      }
    }
  } catch (error) {
    console.error('Error finding workflow by jobId:', error);
  }
  
  return null;
};

// Clear all workflow-specific keys for a session CRITICAL: Also clear any pending debounced writes to prevent race conditions
export const clearWorkflowSessionStorage = (sessionId) => {
  try {
    const keysToRemove = [];
    // sessionId is already "workflow-1", so prefix is just "{sessionId}-"
    const prefix = `${sessionId}-`;
    
    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i);
      if (key && key.startsWith(prefix)) {
        keysToRemove.push(key);
        // CRITICAL: Clear any pending debounced writes for this key This prevents race conditions where a debounced write might save data for the wrong workflow
        clearPendingWrite(key);
      }
    }
    
    keysToRemove.forEach(key => optimizedRemoveItem(key));
  } catch (error) {
    console.error('Error clearing workflow session storage:', error);
  }
};

